import { useMemo } from 'react';

import { useMyStudiesProduct } from 'components/Flux/Personalized/MyStudies/product';
import { AMPERE_COMPANY_ID } from 'helper/flux/myStudiesFilters';
import {
  companyName,
  creatorFilterName,
  creatorsQueryArgs,
} from 'helper/flux/myStudiesFilterOptions';
import { StudyViewer, isValidUserId } from 'helper/flux/myStudyRow';
import {
  MyStudiesFilterChipLabels,
  MyStudiesFilters,
} from 'types/Flux/Personalized/myStudiesFilters';

/**
 * Nomes de empresa e de usuário para os chips do resumo (#1997), que os filtros não guardam
 * (#2000). A casca passa o resultado ao `FilterSummary`.
 *
 * Lê as mesmas entradas de cache do `CompanyFilter` e do `UserPopover` (mesmos argumentos), então
 * não há requisição a mais: o RTK Query divide uma requisição entre as assinaturas.
 * - `companyLabel`: só para admin com uma empresa escolhida que não seja a 1 (o chip mostra sempre
 *   "Casos Ampere" para ela). Cliente nunca pede `/estudos/empresas/`.
 * - `userLabel`: só com um colega escolhido, no escopo da D16 (`creatorsQueryArgs`). O nome da
 *   lista vence o `nome` guardado no filtro (ver `creatorFilterName`).
 *
 * Sem o nome (carregando ou erro), o chip cai no fallback da #1997 ("#<id>", "usuário #<id>").
 * `currentData`, e não `data`: com os filtros mudando por fora (link, voltar), o nome nunca sai da
 * lista de outro escopo. Sem usuário válido (sem JWT), nada é pedido, como a lista de estudos.
 * Os erros são mostrados pelos próprios filtros; aqui não há tratamento nem Sentry.
 *
 * @param filters - Filtros aplicados (`useMyStudiesFilters().filters`).
 * @param viewer - Quem vê a tela: usuário logado e papel.
 * @returns `companyLabel` e `userLabel` para o `FilterSummary`.
 */
export default function useMyStudiesFilterLabels(
  filters: MyStudiesFilters,
  { userId, isAdmin }: StudyViewer,
): MyStudiesFilterChipLabels {
  const { api } = useMyStudiesProduct();
  const { empresa, criadoPor } = filters;
  const hasViewer = isValidUserId(userId);
  const companyId = empresa === 'todas' ? null : empresa.companyId;
  const { currentData: companies } = api.useCompaniesQuery(undefined, {
    skip: !hasViewer || !isAdmin || companyId === null || companyId === AMPERE_COMPANY_ID,
  });

  const creatorsArgs = useMemo(() => creatorsQueryArgs(empresa, isAdmin), [empresa, isAdmin]);
  const { currentData: creators } = api.useCreatorsQuery(creatorsArgs, {
    skip: !hasViewer || typeof criadoPor !== 'object',
  });

  return {
    companyLabel: companyId === null ? undefined : companyName(companies, companyId),
    userLabel: creatorFilterName(criadoPor, creators),
  };
}
